/**
 * Speed conversion utilities.
 * All conversions use m/s as the base (SI) unit internally.
 */

import { Units } from '../../units';

// Conversion factors TO m/s
const MPH_TO_MS = 0.44704;
const KNOTS_TO_MS = 0.514444;
const KMH_TO_MS = 1 / 3.6;

// Conversion factors FROM m/s
const MS_TO_MPH = 1 / MPH_TO_MS;
const MS_TO_KNOTS = 1 / KNOTS_TO_MS;
const MS_TO_KMH = 3.6;

/**
 * Convert a speed value from one unit to m/s (base unit).
 */
export function speedToMs(value: number, fromUnit: string): number {
  switch (fromUnit.toLowerCase().trim()) {
    case 'mph':
    case 'miles per hour':
      return value * MPH_TO_MS;
    case 'kn':
    case 'kt':
    case 'kts':
    case 'knots':
      return value * KNOTS_TO_MS;
    case 'km/h':
    case 'kmh':
    case 'kph':
      return value * KMH_TO_MS;
    case 'm/s':
    case 'ms':
      return value;
    default:
      return value;
  }
}

/**
 * Convert a speed value from m/s to the target unit system.
 */
export function speedFromMs(
  valueMs: number,
  targetSystem: Units,
): { value: number; unit: string } {
  switch (targetSystem) {
    case Units.MARITIME:
      return { value: valueMs * MS_TO_KNOTS, unit: 'kn' };
    case Units.IMPERIAL:
      return { value: valueMs * MS_TO_MPH, unit: 'mph' };
    case Units.METRIC:
    default:
      return { value: valueMs, unit: 'm/s' };
  }
}

/**
 * Convert a speed from a source unit to the user's preferred system.
 */
export function convertSpeed(
  value: number,
  fromUnit: string,
  targetSystem: Units,
): { value: number; unit: string } {
  const ms = speedToMs(value, fromUnit);
  return speedFromMs(ms, targetSystem);
}
